import type { QueryClient } from '@tanstack/react-query';
import { deleteDeviceKey } from '@/api/deviceKey';
import { resetClockOffset } from '@/api/proof';
import { purgeOfflineCache, type StoragePurgeOutcome } from '@/offline/persister';
import { purgeOutbox, type OutboxPurgeOutcome } from '@/features/outbox/purge';
import { retainOutboxForOwner } from '@/features/outbox/store';
import { pendingActions } from './pendingActions';
import { KEEP_EDITED_PREFIX } from '@/features/sieve-editor/keepEditedKey';

/**
 * Everything that has to disappear when a session ends.
 *
 * This is a checklist, not incidental cleanup: on a shared machine, each step
 * skipped is one user's mail rendered to the next one. It runs on an explicit
 * logout in this tab, and on a `logout` broadcast from any other tab.
 *
 * Deliberately *not* here: the session cookies. Two of the three are
 * `HttpOnly`, so only the backend can clear them — `POST /auth/logout` is the
 * one thing that actually ends the session, and it happens before this.
 *
 * The local steps are synchronous and done by the time this returns to the
 * microtask queue; only the service worker's cache purge is a round trip, and
 * the returned `TeardownResult` says whether it truly happened. Callers must
 * not report "everything is cleared" without looking at it.
 *
 * ## The Outbox, and why it is deleted here but asked about somewhere else
 *
 * Step 6 deletes `ols-mail-outbox` (ADR 0058). That database is unlike the
 * other two: it holds whole outgoing messages — recipients, subject, body,
 * attachment bytes — that **the server has never seen**. Leaving it behind
 * gives the next person at a shared machine the previous user's unsent mail;
 * deleting it destroys the only copy of something they wrote.
 *
 * The resolution is not a condition in here. This function is the hands, and
 * by the time it runs the decision has already been taken, one of four ways:
 *
 *  - **An interactive sign-out** is gated in `app/session.tsx`: with anything
 *    queued, `logout()` raises a question instead of signing out, saying how
 *    many messages there are and that this device holds the only copy. Nothing
 *    is deleted unless the user picks the delete.
 *  - **A `logout` broadcast from another tab** carries a decision the same
 *    person already made, in the tab they made it in. Asking again in every
 *    other tab would be asking about mail that is already gone.
 *  - **An identity change on re-auth** cannot ask: the person whose mail it is
 *    has gone, and a *different* person is signed in on this tab as of a
 *    moment ago. Keeping the queue would show them the previous user's
 *    recipients, subjects and bodies in the Outbox view, with an Edit button —
 *    and it could not be delivered anyway, because each record is pinned to
 *    the account it was written in and the backend refuses a `From` that is
 *    not the resolved account's own. So there is nothing to preserve and a
 *    present person to protect: it goes.
 *  - **A revocation (`401 session_revoked`)** does not delete it at all
 *    (`keepOutbox`, owner decision 2026-09-22, ADR 0061 §4). The owner ended
 *    this device's session from another one — a password change, or "Sign
 *    out other sessions" — which says nothing about the mail they queued
 *    here. The queue is kept, stamped with its owner, and sends after the
 *    same person signs back in; anyone else who signs in on this device
 *    neither sees nor sends it. Everything else is torn down as for any
 *    sign-out.
 *
 * **A session that merely expires tears down nothing at all** — `'expired'`
 * only raises the re-auth dialog over the still-mounted app (ADR 0042), and
 * that is the right answer rather than an oversight: an expiry is evidence
 * about a token, not about whether anyone is still at the keyboard, and a
 * timer that deleted unsent mail while its author was fetching coffee would
 * destroy the message without ever having asked. Nothing is signed out, so
 * nothing is left behind by *this* teardown either; a shared machine is made
 * safe by signing out, which now handles the queue honestly.
 */

/** Per-tab preferences that name a mailbox. Not session material, still leaky. */
const ACCOUNT_SCOPED_KEYS = ['ols-mail.active-account'];

/** Prefixes of per-account keys (one key per mailbox id), removed the same way. */
const ACCOUNT_SCOPED_PREFIXES = [KEEP_EDITED_PREFIX];

/** Runtime cache the service worker keeps for `/api/v1` reads (plan §2, §10). */
export const API_CACHE_NAME = 'ols-api-cache';

/** How long sign-out waits for the worker's answer before giving up on it. */
export const PURGE_ACK_TIMEOUT_MS = 2_000;

/**
 * Why the API cache is or is not gone. Every value but `purged` means the
 * cached mail may still be on disk, and nothing may tell the user otherwise.
 */
export type CachePurgeOutcome =
  | 'purged'
  /** This browser has no service worker API (or it is disabled). */
  | 'unsupported'
  /** A worker exists but does not control this page yet — nothing to ask. */
  | 'no-controller'
  /** Asked, never answered within `PURGE_ACK_TIMEOUT_MS`. */
  | 'timed-out'
  /** The worker answered, and said the deletion did not happen. */
  | 'failed';

/** Whether a revocation (`keepOutbox`) also keeps the local draft copies. */
export const KEEP_DRAFTS_ON_REVOCATION = true;

export interface TeardownResult {
  /** True only when a worker confirmed the deletion. Never assumed. */
  cachePurged: boolean;
  cachePurge: CachePurgeOutcome;
  /**
   * True only when the persisted mail cache (IndexedDB) is confirmed gone.
   *
   * There are two copies of a user's mail on disk, not one: the service
   * worker's HTTP cache of API reads, and the query cache persisted for
   * offline reading (ADR 0059). A sign-out that clears one and not the other
   * is not a sign-out on a shared machine.
   */
  storagePurged: boolean;
  storagePurge: StoragePurgeOutcome;
  /**
   * True only when the Outbox database (ADR 0058) is confirmed gone.
   *
   * The third copy, and the only one that is not a copy: these are messages
   * the server has never seen. See the note above for who decided this was
   * allowed to happen before teardown was called.
   */
  outboxPurged: boolean;
  /** `'kept'`: deliberately left in place for its owner (a revocation). */
  outboxPurge: OutboxPurgeOutcome | 'kept';
  /** How many unsent messages the purge destroyed. `0` is the common case. */
  outboxDiscarded: number;
}

export interface TeardownOptions {
  /** Set when the queue's work no longer belongs to whoever is signing in. */
  discardQueuedWork?: boolean;
  /**
   * Keep the Outbox for its owner instead of deleting it (a revocation, ADR
   * 0061 §4). `owner` is the ended session's `account.id`, `undefined` when
   * this tab never learned it (a cold load that was revoked at boot).
   */
  keepOutbox?: { owner: string | undefined };
  /** Overridable for tests; production always uses the constant. */
  purgeTimeoutMs?: number;
}

interface PurgeAck {
  type?: string;
  ok?: boolean;
  deleted?: string[];
  reason?: string;
}

/**
 * Ask the service worker to delete its API cache, and wait for it to say so.
 *
 * The page cannot delete the worker's caches itself, and it must not navigate
 * to the login screen while the purge is still in flight — a worker with no
 * pending `waitUntil` and no client can be killed mid-delete. So this is a
 * round trip over a `MessageChannel`, with a timeout because a wedged worker
 * must not wedge sign-out with it.
 */
export async function purgeServiceWorkerCache(timeoutMs: number): Promise<CachePurgeOutcome> {
  let controller: ServiceWorker | null | undefined;
  try {
    controller = navigator.serviceWorker?.controller;
  } catch {
    return 'unsupported';
  }
  if (navigator.serviceWorker === undefined) return 'unsupported';
  if (!controller) return 'no-controller';

  return await new Promise<CachePurgeOutcome>((resolve) => {
    let settled = false;
    const channel = new MessageChannel();
    const finish = (outcome: CachePurgeOutcome): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      channel.port1.onmessage = null;
      channel.port1.close();
      resolve(outcome);
    };
    const timer = setTimeout(() => finish('timed-out'), timeoutMs);

    channel.port1.onmessage = (event: MessageEvent<PurgeAck>) => {
      const ack = event.data ?? {};
      finish(ack.ok === true ? 'purged' : 'failed');
    };

    try {
      controller.postMessage({ type: 'ols:purge-cache', cacheName: API_CACHE_NAME }, [channel.port2]);
    } catch {
      finish('failed');
    }
  });
}

/**
 * Sign-out reports the purge honestly, including when it did not happen.
 *
 * A failure is not worth a modal — the session is over either way, and the
 * server-side cookies are already gone — but it must be visible, because "you
 * are signed out" reads as "your mail is off this machine" and here it is not.
 */
export function reportCachePurge(result: TeardownResult): TeardownResult {
  if (!result.cachePurged) {
    console.warn(
      `[session] signed out, but the service worker cache of API reads was not purged (${result.cachePurge}). ` +
        'Cached mail metadata may remain in this browser until the worker next starts.',
    );
  }
  if (!result.storagePurged && result.storagePurge !== 'unsupported') {
    console.warn(
      `[session] signed out, but the persisted offline mail cache was not deleted (${result.storagePurge}). ` +
        'Message bodies and lists may remain in this browser’s IndexedDB until another tab releases it.',
    );
  }
  if (
    !result.outboxPurged &&
    result.outboxPurge !== 'unsupported' &&
    result.outboxPurge !== 'kept'
  ) {
    console.warn(
      `[session] signed out, but the Outbox was not deleted (${result.outboxPurge}). ` +
        'Unsent messages — recipients, subject, body and attachments — may remain in this browser’s ' +
        'IndexedDB until another tab releases the database.',
    );
  }
  return result;
}

export async function teardownSession(
  client: QueryClient,
  options: TeardownOptions = {},
): Promise<TeardownResult> {
  // 1. Server state: every cached message, folder, contact and event.
  client.clear();

  // 2. Local preferences that identify the previous mailbox.
  for (const key of ACCOUNT_SCOPED_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage blocked — nothing was written either */
    }
  }
  try {
    const prefixed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key !== null && ACCOUNT_SCOPED_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        prefixed.push(key);
      }
    }
    for (const key of prefixed) localStorage.removeItem(key);
  } catch {
    /* storage blocked — nothing was written either */
  }

  // 3. Queued work, when it cannot legitimately be replayed.
  if (options.discardQueuedWork === true) pendingActions.discardAll();

  // 4. The device key (ADR 0044). Key lifetime *is* session lifetime: a key
  //    that outlived its session would let one thumbprint link two owners of
  //    a shared machine, and the next login generates a fresh one in
  //    milliseconds. Fire-and-forget — nothing downstream waits on the key,
  //    and one that survives a crash here is deleted on the next boot's
  //    login anyway.
  void deleteDeviceKey();
  // The clock offset belongs to the session that reported it; a stale one
  // would push the next session's first proofs out of the ±120 s window.
  resetClockOffset();

  // 5. The persisted offline mail cache (ADR 0059) — the *other* copy of this
  //    user's mail on this disk. `purgeOfflineCache` stops the persister
  //    before it deletes, so a query still settling in step 1's wake cannot
  //    write the mailbox back into the database this just removed. Ahead of
  //    the worker round trip because it is ours to do and cannot be refused
  //    by a wedged worker.
  const storagePurge = await purgeOfflineCache();

  // 6. The Outbox (ADR 0058) — the third store on this disk, and the only one
  //    holding mail the server has never seen. Deleted unless this is a
  //    revocation, which keeps it for its owner; who was asked, and who could
  //    not be, is the long note at the top of this file. Alongside step 5 and
  //    ahead of the worker round trip for the same reason: it is ours to do
  //    and cannot be refused by a wedged worker.
  const outbox =
    options.keepOutbox !== undefined
      ? {
          outcome: 'kept' as const,
          discarded: 0,
          kept: await retainOutboxForOwner(options.keepOutbox.owner),
        }
      : await purgeOutbox();

  // 6b. The composer's local draft copies (#180) -- unsent text, a reply's
  //     quote included. A deliberate sign-out deletes them with the rest of
  //     this user's mail. A revocation keeps them, as it keeps the Outbox
  //     (ADR 0061 §4): a password changed elsewhere must not destroy what the
  //     user typed here, and every record is owner-scoped, so only the same
  //     owner's next sign-in is offered them. KEEP_DRAFTS_ON_REVOCATION is the
  //     one switch if the owner decides otherwise.
  //     Loaded on demand: the store is the composer's, not the first paint's.
  if (options.keepOutbox === undefined || !KEEP_DRAFTS_ON_REVOCATION) {
    await import('@/routes/mail/draftDb').then(({ purgeDraftStore }) => purgeDraftStore());
  }

  // 7. The service worker's HTTP cache of API reads (plan §10). The worker owns
  //    the caches, so this side asks — and then waits for the answer. Steps 1-4
  //    are done by now, deliberately: they are synchronous and must not be held
  //    up by a worker that may never reply.
  const cachePurge = await purgeServiceWorkerCache(options.purgeTimeoutMs ?? PURGE_ACK_TIMEOUT_MS);
  return {
    cachePurged: cachePurge === 'purged',
    cachePurge,
    storagePurged: storagePurge === 'purged',
    storagePurge,
    outboxPurged: outbox.outcome === 'purged',
    outboxPurge: outbox.outcome,
    outboxDiscarded: outbox.discarded,
  };
}
